import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import Icon1 from "@assets/icons/Icon";
import SmallSpinner from "../loading/SmallSpinner";
import TextField from "../textfields/TextField";

type DropDownItem = {
  value: string | number;
  label: string;
  extra_label?: string;
  type?: string;
};

interface DropDownSearchProps {
  label: string;
  disabled?: boolean;
  options?: DropDownItem[];
  value?: string | number;
  onSelect?: (value: string | number) => void;
  onSearchChange?: (query: string) => void;
  onFetchNewOptions?: () => void;
  loadingSearch?: boolean;
  className?: string;
  mandatory?: boolean;
  debounceDelay?: number;
  hasMoreOptions?: boolean;
  category?: string;
  placeholder?: string;
}

export default function DropDownSearch({
  label,
  disabled = false,
  options = [],
  value = "",
  onSelect = () => {},
  onSearchChange = () => {},
  onFetchNewOptions = () => {}, // Function to fetch more options
  loadingSearch = false,
  className = "",
  mandatory = false,
  debounceDelay = 500, // Default 500ms delay
  hasMoreOptions = false, // Flag to indicate if more options are available
  category = "",
  placeholder = "",
}: DropDownSearchProps) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [focus, setHasFocus] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [openUpwards, setOpenUpwards] = useState<boolean | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLInputElement>(null);
  const listRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the trigger button
      if (dropdownRef.current?.contains(target)) {
        return;
      }

      // Check if click is inside the portal dropdown using a more robust method
      const portalElements = document.querySelectorAll(
        "[data-dropdown-portal]",
      );
      for (let i = 0; i < portalElements.length; i++) {
        if (portalElements[i].contains(target)) {
          return;
        }
      }

      // Click is outside both - close the dropdown
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Filter options based on the input query
  const filteredoptions = options.filter((item) =>
    item.label.toLowerCase().includes(query.toLowerCase()),
  );

  const handleSelect = (e: any, item: DropDownItem) => {
    e.stopPropagation(); // Prevents parent click event from firing

    onSelect(item.value);
    setQuery(""); // Clear query after selection
    setIsOpen(false);
    setHasFocus(false);
  };

  const handleInputChange = (value: any) => {
    if (disabled) return;
    setQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      onSearchChange && onSearchChange(value);
    }, debounceDelay);

    if (value) {
      onSelect(""); // Reset selected value if a new query is being typed
    }
  };

  // Handle list scrolling to detect when user is near the end
  const handleListScroll = (e: any) => {
    if (!hasMoreOptions || loadingSearch) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // If we're within 20px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 20) {
      onFetchNewOptions();
    }
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // If there's more space above than below, open upwards
      const shouldOpenUpwards = spaceAbove > spaceBelow && spaceBelow < 200;
      setOpenUpwards(shouldOpenUpwards);

      // Set position relative to viewport
      setDropdownPosition({
        top: shouldOpenUpwards ? rect.top - 8 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  let has_value = value !== "" && value !== null && value !== undefined;
  let disabled_classname =
    " focus:outline-none focus:ring-transparent focus:border-neutral-300 focus:border-1 text-shadow_25 bg-neutral-100 border-neutral-300";

  // Portal dropdown content
  const dropdownContent = isOpen ? (
    <ul
      data-dropdown-portal // Add this
      onScroll={handleListScroll}
      style={{
        position: "fixed",
        top: openUpwards
          ? dropdownPosition.top - 95
          : dropdownPosition.top - 70, // Adjust height as needed
        left: dropdownPosition.left,
        maxWidth: dropdownPosition.width,

        zIndex: 10001, // above BaseModal (z-[10000])
      }}
      className="flex flex-col p-2 w-full min-w-300 bg-white shadow-lg rounded-xl border   sm:text-sm"
    >
      <div className="mb-3">
        <TextField
          label=""
          placeholder="Rechercher..."
          value={query}
          onChange={handleInputChange}
        ></TextField>
      </div>
      <div className=" overflow-y-auto max-h-200">
        {filteredoptions
          .filter((item) => item?.value !== undefined)
          .map((item) => (
            <li
              key={item.value}
              className={`cursor-pointer select-none relative rounded-lg py-2 pl-2 pr-2 ${
                has_value && value === item.value
                  ? "text-black font-semibold"
                  : "text-gray-900 hover:bg-neutral-100"
              }`}
              onMouseDown={(e) => {
                e.preventDefault();

                handleSelect(e, item);
              }}
            >
              <div className="flex options-center">
                <span
                  className={`block text-h2  ${
                    has_value && value === item.value ? "font-regular" : ""
                  }`}
                >
                  {item.label}
                  {item.extra_label && (
                    <span
                      className={`${
                        item.type
                          ? item.type === "particulier"
                            ? "text-purple-400 bg-purple-50"
                            : item.type === "constructor"
                              ? "text-neutral-400 bg-neutral-50"
                              : item.type === "expert"
                                ? "text-green-400 bg-green-50"
                                : "text-blue-400 bg-blue-50"
                          : "text-blue-400 bg-blue-50"
                      } font-light rounded-full px-1 text-h3 ml-1`}
                    >
                      {item.extra_label}
                    </span>
                  )}
                </span>
              </div>
              {has_value && value === item.value ? (
                <span className="absolute inset-y-0 right-0 flex options-center pr-4 py-2">
                  <svg
                    className="h-5 w-5 text-neutral-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 5.707 8.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              ) : null}
            </li>
          ))}
        {filteredoptions.length === 0 && !loadingSearch && (
          <li className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
            Aucun résultat trouvé
          </li>
        )}
        {loadingSearch && (
          <li className="cursor-default select-none relative flex flex-row gap-2 py-2 pl-3 pr-9 text-gray-500">
            Récupération <SmallSpinner size={20} loading={true} />
          </li>
        )}
      </div>
    </ul>
  ) : null;

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="text-h4 text-neutral-400 ">{label}</div>{" "}
      <div
        className={`relative select-none outline-none border-0 ${className}`}
        style={{ width: "" }}
      >
        <div
          ref={dropdownRef}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          tabIndex={0}
          className={`relative w-full px-3 outline-none h-fit bg-white border flex py-3 items-center flex-row justify-between rounded-xl shadow-sm ${
            disabled
              ? disabled_classname
              : mandatory && !has_value && !isOpen
                ? "border-red-300 cursor-pointer border-2 outline-1 outline-red-100"
                : " cursor-pointer bg-transparent border"
          } sm:text-sm`}
        >
          {mandatory && !has_value && !isOpen && (
            <div className="absolute top-0 justify-start h-full options-start flex py-0 flex-row right-8 text-white text-h6">
              <div className="bg-red-300 flex flex-row options-end rounded-b-lg px-1 h-5 pb-0.5">
                Champ obligatoire
              </div>
            </div>
          )}

          {!isOpen && (
            <div className="absolute top-1/3 right-2 px-1">
              <Icon1
                name="down"
                size={15}
                color={mandatory && !has_value && !isOpen ? "red" : "grey"}
              />
            </div>
          )}
          {isOpen && (
            <div className="absolute top-1/3 right-3">
              <Icon1 name="up" size={15} color="grey" />
            </div>
          )}
          <div className="flex flex-row options-center justify-between w-full mr-4 text-wrap ">
            {has_value && (
              <div className="flex flex-row  justify-between options-center w-full">
                <span>
                  {
                    options
                      .filter((item) => item?.value !== undefined)
                      .find((item) => item.value === value)?.label
                  }
                </span>
              </div>
            )}
            {!has_value && (
              <div className="flex flex-row justify-between options-center w-full">
                <div className="text-neutral-400">{label}</div>
              </div>
            )}
          </div>
        </div>

        {/* Render dropdown in portal to escape parent constraints */}
        {typeof window !== "undefined" &&
          createPortal(dropdownContent, document.body)}
      </div>
    </div>
  );
}
